import { serializeArchive } from '../archive';
import type { ArchiveBlobReader, ArchiveResult, ArchiveRows } from '../archive';
import { getBlob } from '../data/blobStore';
import { readWholeProfile } from '../data/repos/profile';
import type { ProfileSnapshot } from '../data/repos/profile';
import { log } from './logger';
import { shareFile } from './shareFile';
import type { ShareRoute } from './shareFile';

/**
 * **BAK-1** — *"One-tap manual export: client-side zip of a full JSON dump plus all audio blobs, via
 * Web Share or download. Retained alongside automatic sync as the user-held copy and the migration
 * path off the platform."*
 *
 * Three lines of glue over three layers that do not know about each other: `data/repos/profile`
 * reads every row, `archive/serialize` turns them into the one byte stream INV-12 names, and
 * `app/shareFile` hands them to the operating system. This module exists to hold the wiring and the
 * two decisions that come with it.
 *
 * ── Decision 1: the bytes are `serializeArchive`'s, untouched ────────────────
 *
 * INV-12 is *"an archive pushed to the backup service is byte-identical to the archive the same
 * client would produce locally for the same scope"*, and design §4 discharges it with one function.
 * So this module does not re-wrap, re-compress, re-order or append anything. The only thing it adds
 * is a **filename**, which is outside the container — §11.7 forbids a generation timestamp *inside*
 * the archive because one timestamp defeats every other determinism control, and a name in the
 * user's Files app is not inside it.
 *
 * ── Decision 2: blob bytes are read one at a time, after the row snapshot ────
 *
 * `readWholeProfile()` takes every row in one readonly transaction but carries audio **metadata**
 * only; the bytes arrive later, one blob per `ArchiveBlobReader` call, which is §11.2's
 * *"reads blobs one at a time … with yields between them"* and bounds peak memory to a single
 * recording rather than a season of them.
 *
 * The cost is honest and small: a blob deleted between the snapshot and its read is reported in
 * `report.missingBlobBytes` rather than crashing the export. The only writer that could do that is
 * an INV-8 cascade, which the user has to confirm; and §13.5's whole argument is that an export must
 * survive a damaged profile rather than refuse to run on one.
 */

const MODULE = 'backup';

/** BAK-1's container. Named here because it is part of what the user receives. */
export const BACKUP_MIME = 'application/zip';
const BACKUP_FILENAME_PREFIX = 'touchline-backup';

/**
 * A filesystem-safe, sortable name.
 *
 * `YYYY-MM-DDTHH-MM-SS`, from the supplied instant — the caller passes the clock in, so this module
 * is testable without one and no wall-clock read hides inside the export path.
 */
export function backupFilename(at: number): string {
  const stamp = new Date(at).toISOString().replace(/[:.]/g, '-').replace(/-\d{3}Z$/, 'Z');
  return `${BACKUP_FILENAME_PREFIX}-${stamp}.zip`;
}

/**
 * Reads one audio recording's bytes for the archive.
 *
 * An **unfinalized** recording has `blob === null` and its bytes still in `chunks` (§8.2 step 2).
 * Assembling them here is what stops a BAK-1 export from silently dropping a recording that a hard
 * jetsam kill interrupted — AUD-3: *"Captured audio is never discarded."* The assembly is verbatim
 * and in arrival order, exactly as `finalizeBlob` would do it, and it does **not** write: the row
 * stays incomplete, because §8.5's `pageshow` pass is what finalizes it and an export is not
 * permitted to change what it is exporting.
 */
export const readArchiveBlob: ArchiveBlobReader = async (id: string) => assembleArchiveBlob(await getBlob(id));

/**
 * The assembly rule itself, over a row from **any** connection.
 *
 * Extracted by build unit B-21 rather than copied. §13.5's recovery export reads its rows through a
 * second connection (`data/recoveryDb.ts` — a schema-less one, because the schema on the device is
 * newer than this bundle's), so it needs this exact rule and cannot reach it through `getBlob`. Two
 * copies would be two answers to *"is an unfinalized recording exported?"*, and the copy that said
 * "no" would be the one running on the day the app could not open the database — which is the day
 * AUD-3's *"captured audio is never discarded"* matters most.
 *
 * @param row an `AudioBlobRow`, structurally. Typed loosely because the recovery path's rows come
 *   off a connection that declares no schema, and a row from a newer version of the app is still a
 *   row this can assemble.
 */
export function assembleArchiveBlob(
  row: { blob?: Blob | null; chunks?: Blob[]; mime?: string } | null | undefined,
): Blob | null {
  if (!row) return null;
  if (row.blob) return row.blob;
  if (!row.chunks || row.chunks.length === 0) return null;
  return new Blob(row.chunks, { type: row.mime ?? '' });
}

/** The snapshot, in the shape `archive/` accepts. Audio metadata satisfies it structurally (§3.1). */
function toArchiveRows(snapshot: ProfileSnapshot): ArchiveRows {
  return {
    teams: snapshot.teams,
    players: snapshot.players,
    matches: snapshot.matches,
    hypotheses: snapshot.hypotheses,
    observations: snapshot.observations,
    flagResolutions: snapshot.flagResolutions,
    setPieces: snapshot.setPieces,
    talkPoints: snapshot.talkPoints,
    matchNotes: snapshot.matchNotes,
    annotations: snapshot.annotations,
    coachCards: snapshot.coachCards,
    ourPlayers: snapshot.ourPlayers,
    audioBlobs: snapshot.audioBlobs,
    templates: snapshot.templates,
    settings: snapshot.settings,
  };
}

/**
 * Builds the BAK-1 archive: every row, every audio blob, and OBS-2's debug log.
 *
 * `archive/` holds no logger by design (§13.5 needs it dependency-free), so **this is where
 * `result.report` is logged**. Nothing in the archive layer forces its use, and an unread report is
 * an orphan blob nobody hears about.
 *
 * @throws {StorageError} on a read failure. `ArchiveError` only for a container the zip format
 *   cannot represent — row-level damage is reported, never thrown (§13.5).
 */
export async function buildBackupArchive(): Promise<ArchiveResult> {
  const snapshot = await readWholeProfile();
  const result = await serializeArchive({
    rows: toArchiveRows(snapshot),
    scope: { kind: 'all' },
    readBlob: readArchiveBlob,
    // OBS-2: "Debug data is included in the backup zip." Only the `all` scope may carry it — a
    // pushed archive that contained the ring would change its own hash on every drain (§11.3).
    logEntries: snapshot.logEntries,
  });

  logArchiveReport(result);
  return result;
}

/**
 * Surfaces everything the serializer found. NFR-8: reported or recovered, never swallowed.
 *
 * Split by severity rather than dumped at one level, because the two say different things: a rounded
 * coordinate is a note, while a missing blob is a recording the user believes they have.
 */
export function logArchiveReport(result: ArchiveResult): void {
  const { report } = result;

  log.info(MODULE, 'built a backup archive', {
    bytes: result.bytes.length,
    blobs: result.blobCount,
    entityCounts: result.entityCounts,
    excludedSettingKeys: report.excludedSettingKeys,
  });

  if (report.missingBlobBytes.length > 0) {
    log.error(MODULE, 'audio rows were archived with no bytes behind them', {
      ids: report.missingBlobBytes,
    });
  }
  if (report.unknownBlobIds.length > 0) {
    log.error(MODULE, 'rows reference audio that has no metadata row (OBS-2 orphan)', {
      ids: report.unknownBlobIds,
    });
  }
  if (report.danglingTeamId !== null) {
    log.error(MODULE, 'a match in this archive references a team that does not exist', {
      teamId: report.danglingTeamId,
    });
  }
  if (report.coercedFieldPaths.length > 0) {
    log.warn(MODULE, 'values could not be represented and were archived as null', {
      paths: report.coercedFieldPaths,
    });
  }
  if (report.roundedFieldPaths.length > 0) {
    log.warn(MODULE, 'geometry was rounded on the way into the archive', {
      paths: report.roundedFieldPaths,
    });
  }
  if (report.exceedsServiceLimit) {
    log.warn(MODULE, 'this archive is larger than the backup service accepts', {
      bytes: result.bytes.length,
    });
  }
}

export interface BackupExport {
  readonly result: ArchiveResult;
  readonly filename: string;
  readonly route: ShareRoute;
}

/**
 * BAK-1 end to end: build, then hand the file to the operating system.
 *
 * @param at the instant the filename is stamped with. Passed in, never sampled here.
 * @throws {ExportError} when the user cancels the share sheet, or when both routes failed. The
 *   archive was built either way, so a caller that wants to retry does not have to rebuild it —
 *   `buildBackupArchive` and `shareFile` are separately callable for exactly that.
 * @throws {StorageError} on a read failure while building.
 */
export async function exportBackup(at: number): Promise<BackupExport> {
  const result = await buildBackupArchive();
  const filename = backupFilename(at);
  const route = await shareFile({
    bytes: result.bytes,
    filename,
    mime: BACKUP_MIME,
    title: 'Touchline backup',
    subject: 'backup',
  });
  return { result, filename, route };
}
